import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Play, Pause, Square, Volume2, VolumeX, SkipForward, Repeat, Repeat1, Download } from 'lucide-react';
import { supabase } from '../lib/supabase';

// Audio base URL - R2 bucket
const AUDIO_BASE = 'https://pub-2855f52337ac4985b064dac367751093.r2.dev/delta-tunes/DELTA';

// Playlist of all audio tracks
const PLAYLIST = [
    `${AUDIO_BASE}/UBRG/Flicker in the Ashes.wav`,
    `${AUDIO_BASE}/UBRG/No-W.wav`,
    `${AUDIO_BASE}/WBRG/March of the Machines.wav`,
    `${AUDIO_BASE}/WBRG/No-U.wav`,
    `${AUDIO_BASE}/WUBG/Eternal Drift.wav`,
    `${AUDIO_BASE}/WUBG/No-R.wav`,
    `${AUDIO_BASE}/WUBR/Eternal Drift.wav`,
    `${AUDIO_BASE}/WUBR/No-G.wav`,
    `${AUDIO_BASE}/WUBRG/Idle But Ready.wav`,
    `${AUDIO_BASE}/WUBRG/WUBRG.wav`,
    `${AUDIO_BASE}/WURG/Boundless Horizon.wav`,
    `${AUDIO_BASE}/WURG/No-B.wav`,
    `${AUDIO_BASE}/bg/Drones in Bloom.wav`,
    `${AUDIO_BASE}/bg/GOLGARI.wav`,
    `${AUDIO_BASE}/br/RAKDOS.wav`,
    `${AUDIO_BASE}/brg/Breathless Collapse.wav`,
    `${AUDIO_BASE}/brg/JUND.wav`,
    `${AUDIO_BASE}/rg/Echoes of the Hunt.wav`,
    `${AUDIO_BASE}/rg/GRUUL.wav`,
    `${AUDIO_BASE}/ub/DIMIR.wav`,
    `${AUDIO_BASE}/ub/Underwater Signals.wav`,
    `${AUDIO_BASE}/ubg/SULTAI.wav`,
    `${AUDIO_BASE}/ubg/Serpents in Silk.wav`,
    `${AUDIO_BASE}/ubr/Grixis.wav`,
    `${AUDIO_BASE}/ubr/Rotten Elegy.wav`,
    `${AUDIO_BASE}/ug/Glow and Grow.wav`,
    `${AUDIO_BASE}/ug/SIMIC.wav`,
    `${AUDIO_BASE}/ur/IZZET.wav`,
    `${AUDIO_BASE}/urg/Howling Wilderness.wav`,
    `${AUDIO_BASE}/urg/TEMUR.wav`,
    `${AUDIO_BASE}/wb/Gilded Cage.wav`,
    `${AUDIO_BASE}/wb/ORZHOV.wav`,
    `${AUDIO_BASE}/wbg/ABZAN.wav`,
    `${AUDIO_BASE}/wbg/Echoes in the Stone.wav`,
    `${AUDIO_BASE}/wbr/MARDU.wav`,
    `${AUDIO_BASE}/wbr/Riders in the Thunder.wav`,
    `${AUDIO_BASE}/wg/Golden Hues.wav`,
    `${AUDIO_BASE}/wg/SELESNYA.wav`,
    `${AUDIO_BASE}/wr/BOROS.wav`,
    `${AUDIO_BASE}/wr/The Unyielding March.wav`,
    `${AUDIO_BASE}/wrg/Beast of the Earth.wav`,
    `${AUDIO_BASE}/wrg/NAYA.wav`,
    `${AUDIO_BASE}/wu/AZORIOUS.wav`,
    `${AUDIO_BASE}/wu/Marble Echoes.wav`,
    `${AUDIO_BASE}/wub/Cold Circuitry.wav`,
    `${AUDIO_BASE}/wub/ESPER.wav`,
    `${AUDIO_BASE}/wug/BANT.wav`,
    `${AUDIO_BASE}/wug/Horizon Line Serenade.wav`,
    `${AUDIO_BASE}/wur/Breath Before the Storm.wav`,
    `${AUDIO_BASE}/wur/JESKAI.wav`,
];

// Extract color identity from a track path
// e.g., '/audio/DELTA/wub/Cold Circuitry.wav' -> ['W', 'U', 'B']
function getTrackColors(path: string): string[] {
    // Match the color folder (e.g., "wub", "WUBRG", "rg")
    const match = path.match(/\/DELTA\/([wubrg]+)\//i);
    if (!match) return []; // Root DELTA.wav has no specific colors (plays for all)

    const colorCode = match[1].toUpperCase();
    return colorCode.split('');
}

// Check if a track matches the deck's color identity
// A track matches if it contains AT LEAST ONE of the deck's colors
function trackMatchesDeck(path: string, deckColors: string[]): boolean {
    // If no deck colors specified, play all tracks
    if (deckColors.length === 0) return true;

    const trackColors = getTrackColors(path);

    // If we couldn't parse colors, include the track
    if (trackColors.length === 0) return true;

    // Check if any deck color is in the track's colors
    return deckColors.some(deckColor =>
        trackColors.includes(deckColor.toUpperCase())
    );
}

interface AudioPlayerProps {
    defaultVolume?: number; // 0-100
    deckColors?: string[]; // e.g., ['W', 'U'] for Azorius
    channelName?: string;  // Twitch channel for overlay broadcast
}

// Color code → guild name mapping
const GUILD_NAMES: Record<string, string> = {
    'W': 'Mono White', 'U': 'Mono Blue', 'B': 'Mono Black', 'R': 'Mono Red', 'G': 'Mono Green',
    'WU': 'Azorius', 'UB': 'Dimir', 'BR': 'Rakdos', 'RG': 'Gruul', 'WG': 'Selesnya',
    'WB': 'Orzhov', 'UR': 'Izzet', 'BG': 'Golgari', 'WR': 'Boros', 'UG': 'Simic',
    'WUB': 'Esper', 'UBR': 'Grixis', 'BRG': 'Jund', 'WRG': 'Naya', 'WUG': 'Bant',
    'WBR': 'Mardu', 'URG': 'Temur', 'WBG': 'Abzan', 'WUR': 'Jeskai', 'UBG': 'Sultai',
    'WUBR': 'Yore-Tiller', 'WUBG': 'Witch-Maw', 'WBRG': 'Dune-Brood',
    'UBRG': 'Glint-Eye', 'WURG': 'Ink-Treader', 'WUBRG': 'All Colors'
};

// Extract track title + color guild + download URL from track URL
function parseTrackUrl(url: string): { title: string; colors: string; guild: string; downloadUrl: string } {
    try {
        const decoded = decodeURIComponent(url);
        const parts = decoded.split('/DELTA/');
        if (parts.length < 2) return { title: 'Unknown', colors: '', guild: '', downloadUrl: url };
        const segments = parts[1].split('/');
        const colors = segments.length > 1 ? segments[0] : '';
        const file = segments[segments.length - 1];
        const title = file.replace(/\.wav$/i, '');
        const guild = GUILD_NAMES[colors.toUpperCase()] || colors.toUpperCase();
        return { title, colors, guild, downloadUrl: url };
    } catch {
        return { title: 'Unknown', colors: '', guild: '', downloadUrl: url };
    }
}

// Track play history for 30-minute no-repeat rule
interface PlayHistory {
    track: string;
    playedAt: number;
}

export function AudioPlayer({ defaultVolume = 50, deckColors = [], channelName }: AudioPlayerProps) {
    // Supabase broadcast channel for now-playing overlay
    const broadcastChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

    // Initialize broadcast channel — resolve channel from prop > localStorage > Supabase auth
    useEffect(() => {
        let cancelled = false;

        // One-time cleanup: remove old key that may have cached wrong value
        if (localStorage.getItem('deckstr_twitch_channel')) {
            localStorage.removeItem('deckstr_twitch_channel');
        }

        async function initBroadcast() {
            // 1. Try prop
            let ch = (channelName || '').toLowerCase();

            // 2. Try localStorage (explicit user setting only)
            if (!ch) ch = (localStorage.getItem('deckstr_broadcast_channel') || '').toLowerCase();

            // 3. Try Supabase auth — look for ANY identity username or display name
            if (!ch) {
                try {
                    const { data: { user } } = await supabase.auth.getUser();
                    if (user) {
                        // a) Twitch identity
                        const twitchId = user.identities?.find(i => i.provider === 'twitch');
                        if (twitchId?.identity_data?.user_name) {
                            ch = twitchId.identity_data.user_name.toLowerCase();
                        }
                        // b) Any other OAuth identity with a username
                        if (!ch) {
                            for (const identity of (user.identities || [])) {
                                const uname = identity.identity_data?.user_name ||
                                    identity.identity_data?.preferred_username ||
                                    identity.identity_data?.full_name;
                                if (uname) {
                                    ch = uname.toLowerCase().replace(/\s+/g, '');
                                    break;
                                }
                            }
                        }
                        // c) display_name from user metadata
                        if (!ch && user.user_metadata?.display_name) {
                            ch = user.user_metadata.display_name.toLowerCase().replace(/\s+/g, '');
                        }
                        console.log(`[AudioPlayer] Auth resolved channel="${ch}" (provider=${twitchId?.provider || user.identities?.[0]?.provider || 'email'})`);

                        // Only cache if we got a real identity-based name (not empty)
                        if (ch) {
                            localStorage.setItem('deckstr_broadcast_channel', ch);
                        }
                    } else {
                        console.log('[AudioPlayer] No authenticated user found');
                    }
                } catch (err) {
                    console.log('[AudioPlayer] Auth check failed:', err);
                }
            }

            if (cancelled || !ch) {
                console.log(`[AudioPlayer] No broadcast channel resolved — set one with: localStorage.setItem('deckstr_broadcast_channel', 'YOUR_TWITCH_NAME')`);
                return;
            }

            console.log(`[AudioPlayer] Broadcasting on channel: now-playing-${ch}`);
            const bc = supabase.channel(`now-playing-${ch}`);
            bc.subscribe((status) => {
                if (cancelled) return;
                console.log(`[AudioPlayer] Channel subscription status: ${status}`);
                if (status === 'SUBSCRIBED') {
                    broadcastChannelRef.current = bc;
                    console.log(`[AudioPlayer] ✅ Ready to broadcast on now-playing-${ch}`);
                }
            });
        }

        initBroadcast();
        return () => {
            cancelled = true;
            if (broadcastChannelRef.current) {
                broadcastChannelRef.current.unsubscribe();
                broadcastChannelRef.current = null;
            }
        };
    }, [channelName]);

    // Broadcast current track to overlay
    const broadcastNowPlaying = useCallback((trackUrl: string, playing: boolean) => {
        if (!broadcastChannelRef.current) return;
        const { title, colors, guild, downloadUrl } = parseTrackUrl(trackUrl);
        broadcastChannelRef.current.send({
            type: 'broadcast',
            event: 'now-playing',
            payload: { title, colors, guild, downloadUrl, source: 'deckstr', isPlaying: playing, timestamp: Date.now() }
        });
    }, []);
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(defaultVolume);
    const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
    const [isRepeatOne, setIsRepeatOne] = useState(false);
    const [playHistory, setPlayHistory] = useState<PlayHistory[]>([]);
    const [hasStarted, setHasStarted] = useState(false); // Track if user has ever clicked play

    // Filter playlist based on deck colors - but don't immediately interrupt playback
    const filteredPlaylist = useMemo(() => {
        return PLAYLIST.filter(track => trackMatchesDeck(track, deckColors));
    }, [deckColors]);

    // Track current shuffled playlist (what's actually playing)
    const [shuffledPlaylist, setShuffledPlaylist] = useState<string[]>(() => {
        // Initial shuffle of full playlist
        const shuffled = [...PLAYLIST];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    });

    // Pending playlist update - will be applied on next track change
    const pendingPlaylistRef = useRef<string[] | null>(null);

    // When deck colors change, prepare a new filtered playlist for NEXT song
    // Do NOT immediately switch - only update the pending playlist
    useEffect(() => {
        const shuffled = [...filteredPlaylist];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        pendingPlaylistRef.current = shuffled;
        // Note: We do NOT setShuffledPlaylist or setCurrentTrackIndex here
        // That only happens on song end or skip
    }, [filteredPlaylist]);

    const currentTrack = shuffledPlaylist[currentTrackIndex] || PLAYLIST[0];

    // Clean up old history (older than 30 minutes)
    const cleanHistory = useCallback(() => {
        const thirtyMinsAgo = Date.now() - (30 * 60 * 1000);
        setPlayHistory(prev => prev.filter(h => h.playedAt > thirtyMinsAgo));
    }, []);

    // Apply pending playlist and get next track
    const applyPendingAndGetNext = useCallback(() => {
        // If there's a pending playlist from deck color changes, apply it now
        if (pendingPlaylistRef.current) {
            const newPlaylist = pendingPlaylistRef.current;
            pendingPlaylistRef.current = null;
            setShuffledPlaylist(newPlaylist);
            // Start at index 0 of new playlist
            return { newPlaylist, nextIndex: 0 };
        }
        return null;
    }, []);

    // Get next valid track (not played in last 30 min if possible)
    const getNextTrackIndex = useCallback((playlist: string[] = shuffledPlaylist) => {
        cleanHistory();
        const thirtyMinsAgo = Date.now() - (30 * 60 * 1000);
        const recentlyPlayed = playHistory
            .filter(h => h.playedAt > thirtyMinsAgo)
            .map(h => h.track);

        // Find tracks not recently played
        const availableIndices = playlist
            .map((track, i) => ({ track, i }))
            .filter(({ track }) => !recentlyPlayed.includes(track))
            .map(({ i }) => i);

        if (availableIndices.length > 0) {
            // Pick random from available (not recently played)
            const randomIdx = Math.floor(Math.random() * availableIndices.length);
            return availableIndices[randomIdx];
        }

        // All tracks played recently, just pick random different from current
        let next = Math.floor(Math.random() * playlist.length);
        if (playlist.length > 1) {
            while (next === currentTrackIndex) {
                next = Math.floor(Math.random() * playlist.length);
            }
        }
        return next;
    }, [shuffledPlaylist, playHistory, currentTrackIndex, cleanHistory]);

    // Handle track end - auto-advance to next random track
    const handleTrackEnd = useCallback(() => {
        console.log('Track ended, isRepeatOne:', isRepeatOne);
        if (isRepeatOne) {
            // Repeat current track
            if (audioRef.current) {
                audioRef.current.currentTime = 0;
                audioRef.current.play().catch(() => { });
            }
        } else {
            // Check if there's a pending playlist from deck color changes
            const pending = applyPendingAndGetNext();
            if (pending) {
                // Apply new playlist and start from its first track
                console.log('Applying pending playlist, starting at index:', pending.nextIndex);
                setPlayHistory(prev => [...prev, { track: currentTrack, playedAt: Date.now() }]);
                setCurrentTrackIndex(pending.nextIndex);
            } else {
                // Move to next track in current playlist
                const nextIndex = getNextTrackIndex();
                console.log('Moving to track index:', nextIndex);
                setPlayHistory(prev => [...prev, { track: currentTrack, playedAt: Date.now() }]);
                setCurrentTrackIndex(nextIndex);
            }
        }
    }, [isRepeatOne, getNextTrackIndex, currentTrack, applyPendingAndGetNext]);

    // Handle audio errors (404, network issues, decode errors) - auto-skip to next track
    const handleAudioError = useCallback(() => {
        console.error('Audio error - track may not exist or failed to load:', currentTrack);
        // Mark this track as played (to avoid retrying immediately) and skip
        setPlayHistory(prev => [...prev, { track: currentTrack, playedAt: Date.now() }]);

        // Skip to next track after a brief delay
        setTimeout(() => {
            const pending = applyPendingAndGetNext();
            if (pending) {
                setCurrentTrackIndex(pending.nextIndex);
            } else {
                const nextIndex = getNextTrackIndex();
                setCurrentTrackIndex(nextIndex);
            }
        }, 500);
    }, [currentTrack, applyPendingAndGetNext, getNextTrackIndex]);

    // Set up event listeners - critical for auto-advance AND error handling
    useEffect(() => {
        const audio = audioRef.current;
        if (audio) {
            const onEnded = () => handleTrackEnd();
            const onError = () => handleAudioError();
            // Also handle stalled/waiting states that might indicate a problem
            const onStalled = () => {
                console.warn('Audio stalled, waiting for data...');
            };

            audio.addEventListener('ended', onEnded);
            audio.addEventListener('error', onError);
            audio.addEventListener('stalled', onStalled);

            return () => {
                audio.removeEventListener('ended', onEnded);
                audio.removeEventListener('error', onError);
                audio.removeEventListener('stalled', onStalled);
            };
        }
    }, [handleTrackEnd, handleAudioError]);

    // Load and play new track when index changes (only if user has started playing)
    useEffect(() => {
        const audio = audioRef.current;
        // Only load audio if user has started playback (lazy-loading)
        if (audio && shuffledPlaylist.length > 0 && hasStarted) {
            const trackUrl = shuffledPlaylist[currentTrackIndex] || PLAYLIST[0];
            console.log('Loading track:', trackUrl);
            audio.src = trackUrl;
            audio.load();
            // Broadcast track change to overlay
            broadcastNowPlaying(trackUrl, isPlaying);
            if (isPlaying) {
                audio.play().catch((err) => {
                    console.error('Failed to play track:', err);
                    // If play fails, try skipping to next track
                    handleAudioError();
                });
            }
        }
    }, [currentTrackIndex, shuffledPlaylist, hasStarted, handleAudioError, broadcastNowPlaying]);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = volume / 100;
        }
    }, [volume]);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.muted = isMuted;
        }
    }, [isMuted]);

    const handlePlay = () => {
        if (audioRef.current) {
            // First time playing - set the src (lazy-load)
            if (!hasStarted) {
                audioRef.current.src = shuffledPlaylist[currentTrackIndex] || PLAYLIST[0];
                audioRef.current.load();
                setHasStarted(true);
            }
            audioRef.current.play().catch(() => { });
            setIsPlaying(true);
            broadcastNowPlaying(currentTrack, true);
        }
    };

    const handlePause = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
            broadcastNowPlaying(currentTrack, false);
        }
    };

    const handleStop = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsPlaying(false);
            broadcastNowPlaying(currentTrack, false);
        }
    };

    const handleSkip = () => {
        setPlayHistory(prev => [...prev, { track: currentTrack, playedAt: Date.now() }]);

        // Check if there's a pending playlist from deck color changes
        const pending = applyPendingAndGetNext();
        if (pending) {
            // Apply new playlist and start from its first track
            setCurrentTrackIndex(pending.nextIndex);
        } else {
            // Move to next track in current playlist
            const nextIndex = getNextTrackIndex();
            setCurrentTrackIndex(nextIndex);
        }
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newVolume = parseInt(e.target.value);
        setVolume(newVolume);
        if (newVolume > 0 && isMuted) {
            setIsMuted(false);
        }
    };

    const toggleMute = () => {
        setIsMuted(!isMuted);
    };

    const toggleRepeat = () => {
        setIsRepeatOne(!isRepeatOne);
    };

    return (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg border border-white/10">
            {/* preload="none" ensures no audio is downloaded until user clicks play */}
            <audio ref={audioRef} preload="none" />

            {/* Play/Pause */}
            <button
                onClick={isPlaying ? handlePause : handlePlay}
                className="p-1 hover:bg-white/10 rounded transition-colors text-cyan-400"
                title={isPlaying ? 'Pause' : 'Play'}
            >
                {isPlaying ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
            </button>

            {/* Stop */}
            <button
                onClick={handleStop}
                className="p-1 hover:bg-white/10 rounded transition-colors text-white/50 hover:text-white"
                title="Stop"
            >
                <Square className="size-3" />
            </button>

            {/* Skip */}
            <button
                onClick={handleSkip}
                className="p-1 hover:bg-white/10 rounded transition-colors text-white/50 hover:text-white"
                title="Skip to next"
            >
                <SkipForward className="size-3.5" />
            </button>

            {/* Repeat One */}
            <button
                onClick={toggleRepeat}
                className={`p-1 hover:bg-white/10 rounded transition-colors ${isRepeatOne ? 'text-amber-400' : 'text-white/50 hover:text-white'}`}
                title={isRepeatOne ? 'Repeat One (ON)' : 'Repeat One (OFF)'}
            >
                {isRepeatOne ? <Repeat1 className="size-3.5" /> : <Repeat className="size-3.5" />}
            </button>

            {/* Mute */}
            <button
                onClick={toggleMute}
                className={`p-1 hover:bg-white/10 rounded transition-colors ${isMuted ? 'text-red-400' : 'text-white/50 hover:text-white'}`}
                title={isMuted ? 'Unmute' : 'Mute'}
            >
                {isMuted ? <VolumeX className="size-3.5" /> : <Volume2 className="size-3.5" />}
            </button>

            {/* Volume Slider */}
            <input
                type="range"
                min="0"
                max="100"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="w-12 h-1 bg-white/20 rounded-lg appearance-none cursor-pointer accent-cyan-400"
                title={`Volume: ${volume}%`}
            />

            {/* Download Song Button */}
            <a
                href={currentTrack}
                download
                className="p-1 hover:bg-white/10 rounded transition-colors text-white/50 hover:text-cyan-400"
                title="Download Song"
            >
                <Download className="size-3.5" />
            </a>
        </div>
    );
}
